import SEO from "@/components/SEO";
import PageHero from "@/components/sections/PageHero";
import CTASection from "@/components/sections/CTASection";
import PlanCard from "@/components/sections/PlanCard";
import { Reveal } from "@/components/motion/Reveal";
import { Btn, Eyebrow, SectionHeading } from "@/components/primitives";
import PilotCard from "@/components/sections/PilotCard";
import { plans, comparisonRows, planNotes } from "@/content/site";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Check, Minus, Info } from "lucide-react";

const planKeys = [
  { key: "found", name: "Get Found" },
  { key: "leads", name: "Get Leads" },
  { key: "cruise", name: "Cruise Control" },
];

function Cell({ value }) {
  if (value === true)
    return (
      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-soft text-mint">
        <Check className="h-4 w-4" strokeWidth={3} />
      </span>
    );
  if (value === false)
    return <Minus className="h-4 w-4 text-border-neutral mx-auto" />;
  return <span className="text-xs font-semibold text-forest">{value}</span>;
}

export default function Plans() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: "Green Circle Group Growth Plans",
    itemListElement: plans.map((p) => ({
      "@type": "Service",
      name: `${p.name} — ${p.subtitle}`,
      description: p.description,
    })),
  };

  return (
    <>
      <SEO
        title="Digital Growth Plans for Trades | Green Circle Group"
        description="Compare Get Found, Get Leads and Cruise Control—three managed growth plans designed for busy Australian trade and home-service businesses."
        path="/plans"
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Subscription Plans"
        heading="Three ways to put your digital growth on cruise control."
        copy="Choose a stronger local foundation, a managed lead engine or complete support from visibility through to booked opportunities."
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Plans", to: "/plans" },
        ]}
      />

      {/* Plan cards */}
      <section className="bg-soft py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-5 items-start">
            {plans.map((plan) => (
              <Reveal key={plan.id} delay={plan.recommended ? 0 : 0.08}>
                <PlanCard plan={plan} showAll />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison — desktop table */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow className="justify-center">Compare</Eyebrow>
            <SectionHeading className="mt-5">
              What&apos;s included at each level
            </SectionHeading>
          </div>

          <div className="mt-14 hidden md:block overflow-hidden rounded-[1.5rem] border border-border-neutral">
            <table className="w-full border-collapse" data-testid="comparison-table">
              <thead>
                <tr className="bg-forest text-white">
                  <th className="text-left p-5 font-bold text-sm w-[40%]">
                    Capability
                  </th>
                  {planKeys.map((p) => (
                    <th key={p.key} className="p-5 font-bold text-sm text-center">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={row.capability}
                    className={i % 2 ? "bg-soft/50" : "bg-white"}
                  >
                    <td className="p-5 text-sm font-medium text-dark-neutral">
                      {row.capability}
                    </td>
                    <td className="p-5 text-center">
                      <Cell value={row.found} />
                    </td>
                    <td className="p-5 text-center">
                      <Cell value={row.leads} />
                    </td>
                    <td className="p-5 text-center">
                      <Cell value={row.cruise} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Comparison — mobile accordions */}
          <div className="mt-10 md:hidden">
            <Accordion type="single" collapsible className="space-y-3">
              {planKeys.map((p) => (
                <AccordionItem
                  key={p.key}
                  value={p.key}
                  className="rounded-2xl border border-border-neutral overflow-hidden"
                >
                  <AccordionTrigger className="px-5 py-4 font-extrabold text-forest hover:no-underline">
                    {p.name}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5">
                    <ul className="space-y-2.5">
                      {comparisonRows.map((row) => (
                        <li
                          key={row.capability}
                          className="flex items-center justify-between gap-3 text-sm"
                        >
                          <span className="text-mid-neutral">
                            {row.capability}
                          </span>
                          <Cell value={row[p.key]} />
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Plan notes */}
      <section className="bg-soft py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <div className="rounded-[1.5rem] bg-white border border-border-neutral p-7 md:p-9">
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 text-mint" />
              <h3 className="font-extrabold text-forest">Important plan notes</h3>
            </div>
            <ul className="mt-5 space-y-3">
              {planNotes.map((n, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-mid-neutral">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-mint shrink-0" />
                  {n}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Specialist entry programs */}
      <section
        data-testid="specialist-entry-programs"
        className="bg-white py-16 md:py-24"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Specialist entry programs</Eyebrow>
            <SectionHeading className="mt-5">
              Need evidence before choosing an ongoing plan?
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              Two specialist pilots exist alongside the standard subscription
              plans. Pilot-specific media, commission, readiness, territory,
              operating-hour and administration conditions are confirmed in a
              proposal.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Reveal>
              <PilotCard
                label="Energy Growth Pilot"
                title="For solar, battery & home-energy retailers"
                description="A four-week Meta campaign designed to test lead quality, conversion and scalability before committing to an ongoing managed lead program."
                ctaLabel="Explore Energy Growth Pilot"
                ctaTo="/energy-growth-pilot"
                testId="plans-pilot-egp"
              />
            </Reveal>
            <Reveal delay={0.08}>
              <PilotCard
                label="Installer Growth Pilot"
                title="For qualified solar & battery installers"
                description="A four-week lead-to-installation-ready sales pilot. Green Circle runs the campaign, closes suitable sales within your approved framework and coordinates administration through to installation-ready handover."
                ctaLabel="Explore Installer Growth Pilot"
                ctaTo="/for/installer-growth-pilot"
                variant="dark"
                testId="plans-pilot-igp"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Plan CTA */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <SectionHeading>
            Not sure which plan fits? Start with the business, not the package.
          </SectionHeading>
          <p className="mt-5 text-lg text-mid-neutral max-w-2xl mx-auto">
            The Growth Assessment helps us understand your current position,
            market, goals and capacity before recommending the most appropriate
            level of support.
          </p>
          <div className="mt-8">
            <Btn to="/contact" size="lg">Book a Free Growth Assessment</Btn>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
